import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateFlowSchedule } from '../api/flows';
import { FlowResponse } from '../types';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

interface Props {
  flow: FlowResponse;
  onClose: () => void;
}

const PRESETS: Array<{ label: string; cron: string }> = [
  { label: 'Every 5 minutes', cron: '*/5 * * * *' },
  { label: 'Every 15 minutes', cron: '*/15 * * * *' },
  { label: 'Every hour', cron: '0 * * * *' },
  { label: 'Daily 02:00', cron: '0 2 * * *' },
  { label: 'Weekly Mon 08:00', cron: '0 8 * * 1' },
];

export function ScheduleModal({ flow, onClose }: Props) {
  const qc = useQueryClient();
  const [cron, setCron] = useState(flow.schedule ?? '*/15 * * * *');
  const [timezone, setTimezone] = useState(flow.scheduleTimezone ?? 'Europe/Berlin');
  const [enabled, setEnabled] = useState(flow.scheduleEnabled ?? false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => updateFlowSchedule(flow.id, {
      cronExpression: cron.trim() === '' ? null : cron.trim(),
      timezone,
      enabled,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flows', flow.id] });
      qc.invalidateQueries({ queryKey: ['flows'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Failed to update schedule.');
    },
  });

  return (
    <Dialog open onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Schedule</DialogTitle>
          <DialogDescription>
            Runs this flow automatically on a cron schedule. The flow must also be activated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Schedule enabled</span>
          </label>

          <div className="space-y-1.5">
            <Label htmlFor="cron" className="text-xs text-muted-foreground">Cron expression</Label>
            <Input
              id="cron"
              type="text"
              value={cron}
              onChange={e => setCron(e.target.value)}
              placeholder="*/15 * * * *"
              className="h-9 font-mono"
            />
            <div className="flex flex-wrap gap-1 pt-1">
              {PRESETS.map(p => (
                <Button
                  key={p.cron}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setCron(p.cron)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tz" className="text-xs text-muted-foreground">Timezone (IANA)</Label>
            <Input
              id="tz"
              type="text"
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
              placeholder="Europe/Berlin"
              className="h-9 font-mono"
            />
          </div>

          {(flow.nextRunAt || flow.lastRunAt) && (
            <div className="text-xs text-muted-foreground space-y-0.5">
              {flow.nextRunAt && (
                <div>
                  Next run: <span className="font-mono text-foreground">{new Date(flow.nextRunAt).toLocaleString()}</span>
                </div>
              )}
              {flow.lastRunAt && (
                <div>
                  Last run: <span className="font-mono text-foreground">{new Date(flow.lastRunAt).toLocaleString()}</span>
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => { setError(null); mutation.mutate(); }}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Saving…' : 'Save schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
